const MAX_IMAGE_DIMENSION = 1920;
const WEBP_QUALITY = 0.82;

/**
 * Reduce customer photo upload size without touching video, audio, documents,
 * animated GIFs, or unsupported browser formats. If the browser cannot decode
 * the image, the original file is returned so sending still works.
 */
export async function optimizeImageForUpload(file) {
  if (!file || !file.type?.startsWith('image/') || file.type === 'image/gif') return file;

  let sourceUrl = null;
  let image = null;
  try {
    sourceUrl = URL.createObjectURL(file);
    image = new Image();
    image.decoding = 'async';
    image.src = sourceUrl;
    await image.decode();

    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return file;
    context.drawImage(image, 0, 0, width, height);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY));
    if (!blob) return file;

    // Keep the original if conversion unexpectedly increases the payload.
    if (blob.size >= file.size && scale === 1 && file.type === 'image/webp') return file;

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.webp`, {
      type: 'image/webp',
      lastModified: Date.now()
    });
  } catch (error) {
    console.warn('Image optimization skipped:', error?.message || error);
    return file;
  } finally {
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
  }
}
